import {LatLng, distanceMeters, offsetByMeters} from "./location-helpers";

export interface SpiderfyItem {
  id: number;
  lat: number;
  lng: number;
}

export interface SpiderfyCluster {
  key: string;
  members: SpiderfyItem[];
  centroid: LatLng;
}

export interface SpiderfyDisplayMarker {
  id: number;
  lat: number;
  lng: number;
  trueLat: number;
  trueLng: number;
  /** Arrow base on the marker circle edge (closest to the tip). */
  baseLat: number;
  baseLng: number;
  /** Leader arrow tip (inset from true when spiderfied so heads do not stack). */
  tipLat: number;
  tipLng: number;
  /** True when this marker is offset from its true location to avoid overlap. */
  isSpiderfied: boolean;
}

/** Matches circular MapImageIcon diameter used for tree markers. */
export const SPIDERFY_MARKER_DIAMETER_PIXELS = 38;
export const SPIDERFY_MARKER_RADIUS_PIXELS = SPIDERFY_MARKER_DIAMETER_PIXELS / 2;
/** Pin asset aspect height for the circular clip (width:height ≈ 46:60). */
export const SPIDERFY_MARKER_PIN_HEIGHT_PIXELS = Math.round(
  SPIDERFY_MARKER_DIAMETER_PIXELS * (60 / 46)
);
/** Length of the default downward arrow beyond the circle edge. */
const DEFAULT_DOWN_ARROW_LENGTH_PIXELS = 21;
const DEFAULT_DOWN_ARROW_LENGTH_METERS = 1.8;

/** Tiny air gap between circle edges when packed edge-to-edge. */
const CIRCLE_EDGE_GAP_PIXELS = 2;
/** Minimum center-to-center distance (edges nearly touching). */
const MIN_CENTER_SEPARATION_PIXELS =
  SPIDERFY_MARKER_DIAMETER_PIXELS + CIRCLE_EDGE_GAP_PIXELS;
/**
 * Initial true-position cluster threshold: when marker circles would collide
 * or sit closer than the tiny edge gap.
 */
const DEFAULT_CLUSTER_THRESHOLD_PIXELS = MIN_CENTER_SEPARATION_PIXELS;

const DEFAULT_METER_THRESHOLD = 1.6;
const DEFAULT_SPIDER_RADIUS_METERS = 2.5;
/** Half of polyline strokeWeight (2) so shaft–circle tests match pixels on screen. */
const ARROW_STROKE_HALF_PIXELS = 1;
/** Direct hit: arrow shaft (incl. stroke) reaches the marker circle edge. */
const CIRCLE_HIT_PIXELS = SPIDERFY_MARKER_RADIUS_PIXELS + ARROW_STROKE_HALF_PIXELS;
/** Min centerline distance so stroked shafts don't visually overlap. */
const SHAFT_MIN_SEPARATION_PIXELS =
  2 * ARROW_STROKE_HALF_PIXELS + CIRCLE_EDGE_GAP_PIXELS;
const NUDGE_CLEARANCE_ITERS = 32;

type Pixel = {x: number; y: number};

const clusterKeyForMembers = (members: SpiderfyItem[]) =>
  [...members.map(m => m.id)].sort((a, b) => a - b).join("-");

const centroidOf = (members: SpiderfyItem[]): LatLng => ({
  lat: members.reduce((sum, m) => sum + m.lat, 0) / members.length,
  lng: members.reduce((sum, m) => sum + m.lng, 0) / members.length,
});

const buildClustersFromGroups = (
  groups: Iterable<SpiderfyItem[]> | Map<string | number, SpiderfyItem[]>
): SpiderfyCluster[] => {
  const memberLists =
    groups instanceof Map ? Array.from(groups.values()) : Array.from(groups);
  return memberLists.map(members => ({
    key: clusterKeyForMembers(members),
    members,
    centroid: centroidOf(members),
  }));
};

/** Union-find clustering by pairwise proximity in meters. */
export const buildSpiderfyClustersByMeters = (
  items: SpiderfyItem[],
  thresholdMeters = DEFAULT_METER_THRESHOLD
): SpiderfyCluster[] => {
  if (items.length === 0) return [];

  const parent = items.map((_, i) => i);
  const find = (i: number): number => {
    if (parent[i] !== i) parent[i] = find(parent[i]);
    return parent[i];
  };
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (distanceMeters(items[i], items[j]) < thresholdMeters) {
        union(i, j);
      }
    }
  }

  const groups = new Map<number, SpiderfyItem[]>();
  items.forEach((item, i) => {
    const root = find(i);
    const group = groups.get(root) ?? [];
    group.push(item);
    groups.set(root, group);
  });

  return buildClustersFromGroups(groups);
};

const toWorldPixel = (
  lat: number,
  lng: number,
  zoom: number,
  projection: google.maps.Projection
): Pixel | null => {
  const point = projection.fromLatLngToPoint(new google.maps.LatLng(lat, lng));
  if (!point) return null;
  const scale = Math.pow(2, zoom);
  return {x: point.x * scale, y: point.y * scale};
};

const offsetByPixels = (
  base: LatLng,
  eastPx: number,
  northPx: number,
  zoom: number,
  projection: google.maps.Projection
): LatLng => {
  const point = projection.fromLatLngToPoint(new google.maps.LatLng(base.lat, base.lng));
  if (!point) return base;
  const scale = Math.pow(2, zoom);
  // World pixel Y increases southward.
  const shifted = new google.maps.Point(
    point.x + eastPx / scale,
    point.y - northPx / scale
  );
  const latLng = projection.fromPointToLatLng(shifted);
  if (!latLng) return base;
  return {lat: latLng.lat(), lng: latLng.lng()};
};

const pixelDistance = (a: Pixel, b: Pixel): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/** Distance from point P to segment AB. */
const pointToSegmentDistance = (p: Pixel, a: Pixel, b: Pixel): number => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const apx = p.x - a.x;
  const apy = p.y - a.y;
  const abLenSq = abx * abx + aby * aby;
  if (abLenSq < 1e-6) return pixelDistance(p, a);
  const t = Math.max(0, Math.min(1, (apx * abx + apy * aby) / abLenSq));
  return pixelDistance(p, {x: a.x + abx * t, y: a.y + aby * t});
};

/** Cluster items whose icons would overlap on screen at the current zoom. */
export const buildSpiderfyClusters = (
  items: SpiderfyItem[],
  map: google.maps.Map | null,
  thresholdPixels = DEFAULT_CLUSTER_THRESHOLD_PIXELS
): SpiderfyCluster[] => {
  if (items.length === 0) return [];

  const projection = map?.getProjection();
  const zoom = map?.getZoom();
  if (!projection || zoom == null) {
    return buildSpiderfyClustersByMeters(items);
  }

  const pixels = items.map(item => toWorldPixel(item.lat, item.lng, zoom, projection));
  const parent = items.map((_, i) => i);
  const find = (i: number): number => {
    if (parent[i] !== i) parent[i] = find(parent[i]);
    return parent[i];
  };
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  for (let i = 0; i < items.length; i++) {
    const a = pixels[i];
    if (!a) continue;
    for (let j = i + 1; j < items.length; j++) {
      const b = pixels[j];
      if (!b) continue;
      if (pixelDistance(a, b) < thresholdPixels) {
        union(i, j);
      }
    }
  }

  const groups = new Map<number, SpiderfyItem[]>();
  items.forEach((item, i) => {
    const root = find(i);
    const group = groups.get(root) ?? [];
    group.push(item);
    groups.set(root, group);
  });

  return buildClustersFromGroups(groups);
};

const spiderPositionAtAngle = (
  origin: LatLng,
  angleRad: number,
  radiusPixels: number,
  radiusMeters: number,
  map: google.maps.Map | null
): LatLng => {
  const east = Math.cos(angleRad);
  const north = Math.sin(angleRad);
  const projection = map?.getProjection();
  const zoom = map?.getZoom();
  if (projection && zoom != null) {
    return offsetByPixels(
      origin,
      east * radiusPixels,
      north * radiusPixels,
      zoom,
      projection
    );
  }
  return offsetByMeters(origin, east * radiusMeters, north * radiusMeters);
};

/**
 * Place a point `pixelDistance`/`meterDistance` from `from` toward `toward`.
 * Falls back to due south when the two points are coincident.
 */
const pointFromToward = (
  from: LatLng,
  toward: LatLng,
  pixelDistance: number,
  meterDistance: number,
  map: google.maps.Map | null,
  fallbackSouth = true
): LatLng => {
  const projection = map?.getProjection();
  const zoom = map?.getZoom();

  if (projection && zoom != null) {
    const fromPx = toWorldPixel(from.lat, from.lng, zoom, projection);
    const towardPx = toWorldPixel(toward.lat, toward.lng, zoom, projection);
    if (!fromPx || !towardPx) return from;

    const dx = towardPx.x - fromPx.x;
    const dy = towardPx.y - fromPx.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1e-3) {
      if (!fallbackSouth) return from;
      return offsetByPixels(from, 0, -pixelDistance, zoom, projection);
    }
    const scale = pixelDistance / dist;
    return offsetByPixels(from, dx * scale, -dy * scale, zoom, projection);
  }

  const dist = distanceMeters(from, toward);
  if (dist < 1e-3) {
    if (!fallbackSouth) return from;
    return offsetByMeters(from, 0, -meterDistance);
  }
  const scale = meterDistance / dist;
  return {
    lat: from.lat + (toward.lat - from.lat) * scale,
    lng: from.lng + (toward.lng - from.lng) * scale,
  };
};

/**
 * Point on the marker circle edge closest to `tip` — the arrow base.
 */
export const arrowBaseOnMarkerEdge = (
  markerCenter: LatLng,
  tip: LatLng,
  map: google.maps.Map | null,
  markerRadiusPixels = SPIDERFY_MARKER_RADIUS_PIXELS
): LatLng => {
  const metersPerPixel =
    DEFAULT_DOWN_ARROW_LENGTH_METERS / DEFAULT_DOWN_ARROW_LENGTH_PIXELS;
  return pointFromToward(
    markerCenter,
    tip,
    markerRadiusPixels,
    markerRadiusPixels * metersPerPixel,
    map
  );
};

/**
 * Default location tip due south of a marker center (tip marks the location).
 */
export const defaultLocationTipFromMarker = (
  markerCenter: LatLng,
  map: google.maps.Map | null,
  markerRadiusPixels = SPIDERFY_MARKER_RADIUS_PIXELS
): LatLng => {
  const metersPerPixel =
    DEFAULT_DOWN_ARROW_LENGTH_METERS / DEFAULT_DOWN_ARROW_LENGTH_PIXELS;
  const south = offsetByMeters(markerCenter, 0, -10);
  return pointFromToward(
    markerCenter,
    south,
    markerRadiusPixels + DEFAULT_DOWN_ARROW_LENGTH_PIXELS,
    (markerRadiusPixels + DEFAULT_DOWN_ARROW_LENGTH_PIXELS) * metersPerPixel,
    map
  );
};

/**
 * Singleton layout: tip sits on the true location, marker sits due north so the
 * down-arrow points at the location.
 */
const singletonDisplayForMember = (
  member: SpiderfyItem,
  map: google.maps.Map | null,
  markerRadiusPixels: number
): SpiderfyDisplayMarker => {
  const tip = {lat: member.lat, lng: member.lng};
  const leaderPixels = markerRadiusPixels + DEFAULT_DOWN_ARROW_LENGTH_PIXELS;
  const leaderMeters =
    leaderPixels * (DEFAULT_DOWN_ARROW_LENGTH_METERS / DEFAULT_DOWN_ARROW_LENGTH_PIXELS);
  // Angle π/2 → due north (east=cos, north=sin).
  const display = spiderPositionAtAngle(tip, Math.PI / 2, leaderPixels, leaderMeters, map);
  const base = arrowBaseOnMarkerEdge(display, tip, map, markerRadiusPixels);
  return {
    id: member.id,
    lat: display.lat,
    lng: display.lng,
    trueLat: member.lat,
    trueLng: member.lng,
    baseLat: base.lat,
    baseLng: base.lng,
    tipLat: tip.lat,
    tipLng: tip.lng,
    isSpiderfied: false,
  };
};

/** True if segments AB and CD properly intersect (not merely share an endpoint). */
const segmentsProperlyIntersect = (
  a: Pixel,
  b: Pixel,
  c: Pixel,
  d: Pixel
): boolean => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const cdx = d.x - c.x;
  const cdy = d.y - c.y;
  const acx = c.x - a.x;
  const acy = c.y - a.y;
  const denom = abx * cdy - aby * cdx;
  if (Math.abs(denom) < 1e-8) return false;
  const t = (acx * cdy - acy * cdx) / denom;
  const u = (acx * aby - acy * abx) / denom;
  const eps = 1e-4;
  return t > eps && t < 1 - eps && u > eps && u < 1 - eps;
};

/** Minimum distance between segments AB and CD. */
const segmentToSegmentDistance = (a: Pixel, b: Pixel, c: Pixel, d: Pixel): number => {
  if (segmentsProperlyIntersect(a, b, c, d)) return 0;
  return Math.min(
    pointToSegmentDistance(a, c, d),
    pointToSegmentDistance(b, c, d),
    pointToSegmentDistance(c, a, b),
    pointToSegmentDistance(d, a, b)
  );
};

type DisplayPixels = {
  id: number;
  center: Pixel;
  base: Pixel;
  tip: Pixel;
};

/**
 * True when two markers have a direct visual collision: circles overlapping,
 * an arrow shaft hitting a circle, shafts crossing, or shafts too close.
 */
const markersVisuallyCollide = (
  aCenter: Pixel,
  aBase: Pixel,
  aTip: Pixel,
  bCenter: Pixel,
  bBase: Pixel,
  bTip: Pixel
): boolean => {
  if (pixelDistance(aCenter, bCenter) < MIN_CENTER_SEPARATION_PIXELS) return true;
  if (pointToSegmentDistance(aCenter, bBase, bTip) < CIRCLE_HIT_PIXELS) return true;
  if (pointToSegmentDistance(bCenter, aBase, aTip) < CIRCLE_HIT_PIXELS) return true;
  if (segmentsProperlyIntersect(aBase, aTip, bBase, bTip)) return true;
  // Tips closer than the min shaft separation (incl. fully stacked trues): the
  // shafts end inside the required gap, so the separation is unsatisfiable —
  // treating it as a collision would push the pair apart forever without ever
  // clearing it.
  if (pixelDistance(aTip, bTip) < SHAFT_MIN_SEPARATION_PIXELS) return false;
  return segmentToSegmentDistance(aBase, aTip, bBase, bTip) < SHAFT_MIN_SEPARATION_PIXELS;
};

/**
 * Place markers at their default true-location pins, then translate just enough
 * so circles / shafts sit edge-to-edge (no radial fan-out jump).
 */
export const buildSpiderfyDisplayMarkers = (
  clusters: SpiderfyCluster[],
  map: google.maps.Map | null,
  _spiderRadiusPixels = 34,
  _spiderRadiusMeters = DEFAULT_SPIDER_RADIUS_METERS,
  _minLeaderPixels = 34,
  _minLeaderMeters = 2,
  markerRadiusPixels = SPIDERFY_MARKER_RADIUS_PIXELS
): SpiderfyDisplayMarker[] => {
  const displays = clusters.flatMap(cluster =>
    cluster.members.map(member =>
      singletonDisplayForMember(member, map, markerRadiusPixels)
    )
  );
  return nudgeDisplaysClearOfOverlaps(displays, map);
};

const closestPointOnSegment = (p: Pixel, a: Pixel, b: Pixel): Pixel => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const apx = p.x - a.x;
  const apy = p.y - a.y;
  const abLenSq = abx * abx + aby * aby;
  if (abLenSq < 1e-6) return a;
  const t = Math.max(0, Math.min(1, (apx * abx + apy * aby) / abLenSq));
  return {x: a.x + abx * t, y: a.y + aby * t};
};

/**
 * Translate marker centers (tips fixed) until no circle–circle or circle–shaft
 * overlaps remain. Optional `frozenId` stays put (while dragging, and on drag
 * end so the dropped pin doesn't jump away from the release center).
 */
export const nudgeDisplaysClearOfOverlaps = (
  displays: SpiderfyDisplayMarker[],
  map: google.maps.Map | null,
  frozenId?: number
): SpiderfyDisplayMarker[] => {
  const projection = map?.getProjection();
  const zoom = map?.getZoom();
  if (!projection || zoom == null || displays.length < 2) return displays;

  const working: SpiderfyDisplayMarker[] = displays.map(d => ({...d}));

  const refreshGeometry = (d: SpiderfyDisplayMarker): SpiderfyDisplayMarker => {
    const tip = {lat: d.tipLat, lng: d.tipLng};
    const center = {lat: d.lat, lng: d.lng};
    const base = arrowBaseOnMarkerEdge(center, tip, map);
    const leaderPixels = SPIDERFY_MARKER_RADIUS_PIXELS + DEFAULT_DOWN_ARROW_LENGTH_PIXELS;
    const leaderMeters =
      leaderPixels * (DEFAULT_DOWN_ARROW_LENGTH_METERS / DEFAULT_DOWN_ARROW_LENGTH_PIXELS);
    const defaultCenter = spiderPositionAtAngle(
      tip,
      Math.PI / 2,
      leaderPixels,
      leaderMeters,
      map
    );
    const isDefaultDownArrow =
      Math.abs(center.lat - defaultCenter.lat) < 1e-7 &&
      Math.abs(center.lng - defaultCenter.lng) < 1e-7;
    return {
      ...d,
      baseLat: base.lat,
      baseLng: base.lng,
      isSpiderfied: frozenId != null && d.id === frozenId
        ? d.isSpiderfied
        : !isDefaultDownArrow,
    };
  };

  const pixelsFor = (d: SpiderfyDisplayMarker): DisplayPixels | null => {
    const tip = {lat: d.tipLat, lng: d.tipLng};
    const center = {lat: d.lat, lng: d.lng};
    const base = arrowBaseOnMarkerEdge(center, tip, map);
    const centerPx = toWorldPixel(center.lat, center.lng, zoom, projection);
    const basePx = toWorldPixel(base.lat, base.lng, zoom, projection);
    const tipPx = toWorldPixel(tip.lat, tip.lng, zoom, projection);
    if (!centerPx || !basePx || !tipPx) return null;
    return {id: d.id, center: centerPx, base: basePx, tip: tipPx};
  };

  const moveIndex = (idx: number, eastPx: number, northPx: number): void => {
    const d = working[idx];
    const next = offsetByPixels(
      {lat: d.lat, lng: d.lng},
      eastPx,
      northPx,
      zoom,
      projection
    );
    working[idx] = {...d, lat: next.lat, lng: next.lng};
  };

  const pushAlong = (
    aIdx: number,
    bIdx: number,
    freezeA: boolean,
    freezeB: boolean,
    ux: number,
    uy: number,
    need: number
  ): boolean => {
    if (need <= 0 || (freezeA && freezeB)) return false;
    if (freezeA && !freezeB) {
      moveIndex(bIdx, ux * need, -uy * need);
      return true;
    }
    if (freezeB && !freezeA) {
      moveIndex(aIdx, -ux * need, uy * need);
      return true;
    }
    const half = need / 2;
    moveIndex(aIdx, -ux * half, uy * half);
    moveIndex(bIdx, ux * half, -uy * half);
    return true;
  };

  /**
   * During drag, scooch the movable marker sideways (perpendicular to its
   * tip→center arrow) instead of shoving it along the shaft (which lengthens
   * the arrow when approached from below).
   */
  const scoochSidewaysFromFrozen = (
    movableIdx: number,
    frozenIdx: number,
    stepPixels: number
  ): boolean => {
    if (stepPixels <= 0) return false;
    const movable = pixelsFor(working[movableIdx]);
    const frozen = pixelsFor(working[frozenIdx]);
    if (!movable || !frozen) return false;

    // Perpendicular to tip → center (world Y south-positive).
    let sx = movable.center.x - movable.tip.x;
    let sy = movable.center.y - movable.tip.y;
    let slen = Math.hypot(sx, sy);
    if (slen < 1e-3) {
      sx = 0;
      sy = -1; // default up-from-tip
      slen = 1;
    } else {
      sx /= slen;
      sy /= slen;
    }
    let px = -sy;
    let py = sx;

    // Move away from the frozen marker on the lateral axis.
    const fx = frozen.center.x - movable.center.x;
    const fy = frozen.center.y - movable.center.y;
    const side = fx * px + fy * py;
    if (side > 0 || (Math.abs(side) < 1e-3 && fx > 0)) {
      px = -px;
      py = -py;
    } else if (Math.abs(side) < 1e-3 && fx === 0) {
      // Directly below/above with no horizontal hint — prefer +X scooch.
      px = 1;
      py = 0;
    }

    moveIndex(movableIdx, px * stepPixels, -py * stepPixels);
    return true;
  };

  const resolvePair = (aIdx: number, bIdx: number): boolean => {
    const a = working[aIdx];
    const b = working[bIdx];
    const ap = pixelsFor(a);
    const bp = pixelsFor(b);
    if (!ap || !bp) return false;
    if (!markersVisuallyCollide(ap.center, ap.base, ap.tip, bp.center, bp.base, bp.tip)) {
      return false;
    }

    const freezeA = frozenId != null && a.id === frozenId;
    const freezeB = frozenId != null && b.id === frozenId;

    // Drag: scooch the free marker sideways rather than extending its arrow.
    if (freezeA !== freezeB) {
      const movableIdx = freezeA ? bIdx : aIdx;
      const frozenIdx = freezeA ? aIdx : bIdx;
      const mov = freezeA ? bp : ap;
      const frz = freezeA ? ap : bp;
      const centerDist = pixelDistance(mov.center, frz.center);

      if (centerDist < MIN_CENTER_SEPARATION_PIXELS) {
        // Lateral distance needed so sqrt(dist^2 + s^2) >= separation when
        // moving perpendicular to the current center line is approximated by
        // sideways scooch; use exact lateral need for vertical stacks.
        const lateralNeed = Math.sqrt(
          Math.max(
            0,
            MIN_CENTER_SEPARATION_PIXELS * MIN_CENTER_SEPARATION_PIXELS -
              centerDist * centerDist
          )
        );
        return scoochSidewaysFromFrozen(
          movableIdx,
          frozenIdx,
          Math.max(lateralNeed, 1.5)
        );
      }

      // Shaft / crossing hit — step sideways until clear on later passes.
      return scoochSidewaysFromFrozen(movableIdx, frozenIdx, 2);
    }

    // Idle (no frozen marker): separate along center / shaft normals.
    let dist = pixelDistance(ap.center, bp.center);
    if (dist < MIN_CENTER_SEPARATION_PIXELS) {
      let ux = 1;
      let uy = 0;
      if (dist >= 1e-3) {
        ux = (bp.center.x - ap.center.x) / dist;
        uy = (bp.center.y - ap.center.y) / dist;
      } else {
        dist = 0;
      }
      return pushAlong(
        aIdx,
        bIdx,
        false,
        false,
        ux,
        uy,
        MIN_CENTER_SEPARATION_PIXELS - dist
      );
    }

    const aShaftHitsB =
      pointToSegmentDistance(bp.center, ap.base, ap.tip) < CIRCLE_HIT_PIXELS;
    const bShaftHitsA =
      pointToSegmentDistance(ap.center, bp.base, bp.tip) < CIRCLE_HIT_PIXELS;

    if (aShaftHitsB) {
      const hitDist = pointToSegmentDistance(bp.center, ap.base, ap.tip);
      const closest = closestPointOnSegment(bp.center, ap.base, ap.tip);
      let dx = bp.center.x - closest.x;
      let dy = bp.center.y - closest.y;
      let len = Math.hypot(dx, dy);
      if (len < 1e-3) {
        dx = bp.center.x - ap.center.x;
        dy = bp.center.y - ap.center.y;
        len = Math.hypot(dx, dy) || 1;
      }
      return pushAlong(
        aIdx,
        bIdx,
        false,
        false,
        dx / len,
        dy / len,
        CIRCLE_HIT_PIXELS - hitDist + 0.5
      );
    }

    if (bShaftHitsA) {
      const hitDist = pointToSegmentDistance(ap.center, bp.base, bp.tip);
      const closest = closestPointOnSegment(ap.center, bp.base, bp.tip);
      let dx = ap.center.x - closest.x;
      let dy = ap.center.y - closest.y;
      let len = Math.hypot(dx, dy);
      if (len < 1e-3) {
        dx = ap.center.x - bp.center.x;
        dy = ap.center.y - bp.center.y;
        len = Math.hypot(dx, dy) || 1;
      }
      return pushAlong(
        aIdx,
        bIdx,
        false,
        false,
        -dx / len,
        -dy / len,
        CIRCLE_HIT_PIXELS - hitDist + 0.5
      );
    }

    // Shaft–shaft too close (parallel overlap on zoom, crossings, etc.).
    const shaftDist = segmentToSegmentDistance(ap.base, ap.tip, bp.base, bp.tip);
    if (shaftDist < SHAFT_MIN_SEPARATION_PIXELS) {
      // Direction from a point on A's shaft toward the closest point on B's.
      const onB = closestPointOnSegment(ap.base, bp.base, bp.tip);
      const onA = closestPointOnSegment(onB, ap.base, ap.tip);
      let dx = onB.x - onA.x;
      let dy = onB.y - onA.y;
      let len = Math.hypot(dx, dy);
      if (len < 1e-3 && dist >= 1e-3) {
        dx = bp.center.x - ap.center.x;
        dy = bp.center.y - ap.center.y;
        len = dist;
      } else if (len < 1e-3) {
        dx = 1;
        dy = 0;
        len = 1;
      }
      return pushAlong(
        aIdx,
        bIdx,
        false,
        false,
        dx / len,
        dy / len,
        SHAFT_MIN_SEPARATION_PIXELS - shaftDist + 0.5
      );
    }

    return false;
  };

  for (let pass = 0; pass < NUDGE_CLEARANCE_ITERS; pass++) {
    let moved = false;
    for (let i = 0; i < working.length; i++) {
      for (let j = i + 1; j < working.length; j++) {
        if (resolvePair(i, j)) moved = true;
      }
    }
    if (!moved) break;
  }

  // Always recompute bases at the current zoom so shafts stay on circle edges.
  return working.map(d => refreshGeometry(d));
};

/**
 * While dragging `draggedId`, nudge other markers just enough to clear circle
 * and arrow-shaft collisions. Tips stay on each marker's true location.
 */
export const resolveDisplaysAroundDrag = (
  baselineDisplays: SpiderfyDisplayMarker[],
  draggedId: number,
  dragCenter: LatLng,
  map: google.maps.Map | null
): SpiderfyDisplayMarker[] => {
  const dragTip = defaultLocationTipFromMarker(dragCenter, map);
  const dragBase = arrowBaseOnMarkerEdge(dragCenter, dragTip, map);

  const seeded = baselineDisplays.map(d => {
    if (d.id !== draggedId) {
      // Keep tip on the true location so shafts stay meaningful while nudging.
      return {
        ...d,
        tipLat: d.trueLat,
        tipLng: d.trueLng,
      };
    }
    return {
      ...d,
      lat: dragCenter.lat,
      lng: dragCenter.lng,
      baseLat: dragBase.lat,
      baseLng: dragBase.lng,
      tipLat: dragTip.lat,
      tipLng: dragTip.lng,
    };
  });

  return nudgeDisplaysClearOfOverlaps(seeded, map, draggedId);
};

/**
 * Default rest display for a marker's current true location (down-arrow layout).
 * Used to return drag-nudged neighbors to rest so displacement from one drag
 * doesn't become the baseline of the next.
 */
export const restDisplayForTrueLocation = (
  d: SpiderfyDisplayMarker,
  map: google.maps.Map | null,
  markerRadiusPixels = SPIDERFY_MARKER_RADIUS_PIXELS
): SpiderfyDisplayMarker =>
  singletonDisplayForMember({id: d.id, lat: d.trueLat, lng: d.trueLng}, map, markerRadiusPixels);

/**
 * Apply a finished drag: keep display positions from the last drag frame, and
 * commit the dragged marker's new true location (tip under the dropped center).
 */
export const commitDisplaysAfterDrag = (
  dragDisplays: SpiderfyDisplayMarker[],
  draggedId: number,
  dragCenter: LatLng,
  map: google.maps.Map | null
): SpiderfyDisplayMarker[] => {
  const tip = defaultLocationTipFromMarker(dragCenter, map);
  const base = arrowBaseOnMarkerEdge(dragCenter, tip, map);
  return dragDisplays.map(d => {
    if (d.id !== draggedId) return d;
    return {
      ...d,
      lat: dragCenter.lat,
      lng: dragCenter.lng,
      trueLat: tip.lat,
      trueLng: tip.lng,
      tipLat: tip.lat,
      tipLng: tip.lng,
      baseLat: base.lat,
      baseLng: base.lng,
      isSpiderfied: false,
    };
  });
};

/** True when preserved display true-positions match the current tree locations. */
export const displaysMatchTreeTrues = (
  displays: SpiderfyDisplayMarker[],
  trees: Array<{id: number; lat: number; lng: number}>
): boolean => {
  if (displays.length !== trees.length) return false;
  const byId = new Map(displays.map(d => [d.id, d]));
  for (const tree of trees) {
    const d = byId.get(tree.id);
    if (!d) return false;
    if (Math.abs(d.trueLat - tree.lat) > 1e-9 || Math.abs(d.trueLng - tree.lng) > 1e-9) {
      return false;
    }
  }
  return true;
};
